import React, { useContext } from "react";
import axios from "axios";
import AuthContext from "../../store/AuthContext";
import { authConfig, checkedData, portalApiBase } from "../../services/portalApi";
import PortalQuoteSettings from "./PortalQuoteSettings";
import PortalQuoteSettingStatus from "./PortalQuoteSettingStatus";
import { useEffect, useState, useRef } from "react";
import Button from "../ui/Button";
import usePost from "../../hooks/usePost";
import { currencyList } from "../../data/currencyList";
import chevronIcon from "../../assets/icons/chevron-down-white.svg";
import SelectInput from "../ui/SelectInput";
import TextInput from "../ui/TextInput";
import { isDealPreAvinode } from "../../utilities/settings";
import format from "date-fns/format";
import PortalSalesStageBasedCtas from "./PortalSalesStageBasedCtas";
import PortalQuoteOptions from "./PortalQuoteOptions";
import LoadingOverlay from "../shared/LoadingOverlay";
import TextAreaInput from "../ui/TextAreaInput";
import CloseCross from "../ui/CloseCross";
import { getTripTypeLabelFromLegCount } from "../../utilities/tripTypeDisplay";
import PortalDealDetailsEditToolbar from "./PortalDealDetailsEditToolbar";
import Switch from "../ui/Switch";
import { yesNoList } from "../../data/portalFormListData";
import { toYesNoDisplay } from "../../utilities/commercialChecklist";
import { useSearchParams } from "react-router-dom";

const PortalSalesDetails = ({ deal, legs, onDeleteLeg }) => {
    const { sending, postData, returnMessage } = usePost();
    const authCtx = useContext(AuthContext);
    const [savingSetting, setSavingSetting] = useState(null);
    const settingsSaving = savingSetting !== null;
    const settingsSavingRef = useRef(false);
    const [settingsError, setSettingsError] = useState("");
    const [savedSettings, setSavedSettings] = useState({
        Show_MOVIDA_in_Quote: !!deal?.show_MOVIDA_in_Quote,
        Preferred_Currency: deal?.preferred_Currency,
    });
    const pendingSettings = useRef(null);

    useEffect(() => {
        if (returnMessage.success && returnMessage.postRef === "updateSalesDeal" && pendingSettings.current) {
            setSavedSettings(pendingSettings.current);
        }
    }, [returnMessage]);

    const saveQuoteSettings = async (patch) => {
        if (settingsSavingRef.current) return;
        settingsSavingRef.current = true;
        setSavingSetting("Show_MOVIDA_in_Quote" in patch ? "movida" : "currency");
        setSettingsError("");
        try {
            checkedData(
                await axios.post(
                    `${portalApiBase}/update-deal`,
                    { dealId: deal.id, ...patch },
                    authConfig(authCtx?.token)
                )
            );
            setSavedSettings((previous) => ({ ...previous, ...patch }));
            if ("Show_MOVIDA_in_Quote" in patch) setSelMovida(patch.Show_MOVIDA_in_Quote);
            if ("Preferred_Currency" in patch) setSelectedPrefCurrency(patch.Preferred_Currency);
        } catch {
            setSettingsError("Quote settings could not be saved. Please try again.");
        } finally {
            settingsSavingRef.current = false;
            setSavingSetting(null);
        }
    };
    const legData = useRef({});
    const [paramLegs, setParamLegs] = useState(legs);
    const [selectedKycHeat, setSelectedKycHeat] = useState("");
    const [selectedPrefCurrency, setSelectedPrefCurrency] = useState("");
    const [selMovida, setSelMovida] = useState("");
    const [numPets, setNumPets] = useState(0);
    const [currentNotes, setCurrentNotes] = useState("");
    const [contractName, setContractName] = useState("");
    const [quoteName, setQuoteName] = useState("");
    const [uplift, setUplift] = useState("");
    const [commercialNotes, setCommercialNotes] = useState("");
    const [shopped, setShopped] = useState("");

    // Aircraft-related state
    const [aircraftTail, setAircraftTail] = useState("");
    const [aircraftOp, setAircraftOp] = useState("");
    const [aircraftType, setAircraftType] = useState("");
    const [aircraftCat, setAircraftCat] = useState("");
    const [selectedWifi, setSelectedWifi] = useState("");
    const [selectedCatering, setSelectedCatering] = useState("");
    const [currentOpsNotes, setCurrentOpsNotes] = useState("");
    const [numPilots, setNumPilots] = useState(0);
    const [numFuelStops, setNumFuelStops] = useState(0);
    const [hasFlightAttendant, setHasFlightAttendant] = useState("");
    const [FFOverride, setFFOverride] = useState(false);

    // Expandable leg sections
    const [expandedLegs, setExpandedLegs] = useState({});

    const [didSetDeal, setDidSetDeal] = useState(false);

    const [searchParams] = useSearchParams();
    const openedFromBrokerLink = searchParams.get("preview") === "quote";
    const [showResultOptions, setShowResultOptions] = useState(openedFromBrokerLink);

    const [firstLeg, setFirstLeg] = useState(null);

    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (legs && legs.length > 0) {
            const tmpLegs = legs.map((leg) => ({
                id: leg.id,
                airport_From: leg.airport_From,
                airport_To: leg.airport_To,
                number_of_Bags: leg.number_of_Bags,
                number_of_Carry_On_Bags: leg.number_of_Carry_On_Bags,
                number_of_Passengers: leg.number_of_Passengers,
                Leg_Date_and_Time: leg.leg_Date_and_Time,
                daM_Ref: leg.daM_Ref,
            }));
            tmpLegs.forEach((l) => {
                legData.current[l.id] = l;
            });

            setParamLegs(legs);

            if (!isDealPreAvinode(deal)) {
                const theFirstLeg = [...legs].sort((a, b) => {
                    return a.leg_Number < b.leg_Number ? -1 : 1;
                })[0];
                setFirstLeg(theFirstLeg);
            }
        }
    }, [legs, deal]);

    // Expand all legs by default
    useEffect(() => {
        if (paramLegs && paramLegs.length > 0) {
            const initialExpanded = {};
            paramLegs.forEach((leg) => {
                initialExpanded[leg.id] = true;
            });
            setExpandedLegs(initialExpanded);
        }
    }, [paramLegs]);

    useEffect(() => {
        if (deal && !didSetDeal) {
            setSavedSettings({
                Show_MOVIDA_in_Quote: !!deal.show_MOVIDA_in_Quote,
                Preferred_Currency: deal.preferred_Currency,
            });
            setSelectedKycHeat(deal.kyC_Heat);
            setSelectedPrefCurrency(deal.preferred_Currency);
            setSelMovida(deal.show_MOVIDA_in_Quote);
            setNumPets(deal.number_of_Pets ?? 0);
            setCurrentNotes(deal.notes_on_Flight);
            setContractName(deal.contract_Display_Name);
            setQuoteName(deal.quote_Display_Name);
            setUplift(deal.uplift ?? "");
            setCommercialNotes(deal.commercial_Notes ?? "");
            setShopped(toYesNoDisplay(deal.shopped) ?? "");

            // Set aircraft-related fields
            setAircraftTail(deal.tail_No || "");
            setAircraftOp(
                deal.flight_Operator_Test && deal.flight_Operator_Test.length > 0 ? deal.flight_Operator_Test[0] : ""
            );
            setAircraftType(deal.aircraft_Type_NEW || "");
            setAircraftCat(deal.aircraft_Category_NEW || "");
            setSelectedWifi(deal.wiFi || "");
            setSelectedCatering(deal.catering_option || "");
            setCurrentOpsNotes(deal.catering || "");
            setNumPilots(deal.pilots ?? 0);
            setNumFuelStops(deal.fuel_Stops ?? 0);
            setHasFlightAttendant(deal.has_Flight_Attendant || "");
            setFFOverride(deal.fF_override || false);

            // Expand first leg by default
            if (legs && legs.length > 0) {
                const firstLegId = legs.sort((a, b) => {
                    return a.leg_Number < b.leg_Number ? -1 : 1;
                })[0].id;
                setExpandedLegs({ [firstLegId]: true });
            }

            setDidSetDeal(true);
        }
    }, [deal, legs, didSetDeal]);

    const toggleLeg = (legId) => {
        setExpandedLegs((prev) => ({
            ...prev,
            [legId]: !prev[legId],
        }));
    };

    const saveLegs = () => {
        const updatedLegData = Object.keys(legData.current).map((key) => legData.current[key]);
        postData({ legs: updatedLegData, dealId: deal.id }, "/api/portal/update-deal-legs");
    };

    const updateLegHandler = (leg, key, e) => {
        if (!legData.current[leg.id]) return;
        legData.current[leg.id][key] = e.target.value;
    };

    const updateLegDateHandler = (leg, key, e) => {
        if (!legData.current[leg.id]) return;
        const currentDate = new Date(leg.leg_Date_and_Time);
        switch (key) {
            case "date": {
                const newDateStr = e.target.value;
                const hours = currentDate.getHours();
                const minutes = currentDate.getMinutes();
                const seconds = currentDate.getSeconds();
                const milliseconds = currentDate.getMilliseconds();
                const [year, month, day] = newDateStr.split("-").map(Number);
                const newDateTime = new Date(year, month - 1, day, hours, minutes, seconds, milliseconds);
                const dstr = format(newDateTime, "yyyy-MM-dd'T'HH:mm:'00.000Z'");
                legData.current[leg.id].Leg_Date_and_Time = dstr;
                break;
            }
            case "time": {
                const newTime = e.target.value;
                const year = currentDate.getFullYear();
                const month = currentDate.getMonth();
                const day = currentDate.getDate();
                const [hours, minutes] = newTime.split(":").map(Number);
                const newDateTime = new Date(year, month, day, hours, minutes);
                const dstr = format(newDateTime, "yyyy-MM-dd'T'HH:mm:'00.000Z'");
                legData.current[leg.id].Leg_Date_and_Time = dstr;
                break;
            }
            default:
                break;
        }
    };

    const updateDeal = () => {
        let formData = {};

        formData = {
            dealId: deal.id,
            KYC_Heat: selectedKycHeat,
            Preferred_Currency: selectedPrefCurrency,
            Show_MOVIDA_in_Quote: selMovida,
            Number_of_Pets: numPets,
            Notes_on_Flight: currentNotes,
            Contract_Display_Name: contractName,
            Quote_Display_Name: quoteName,
            Uplift: uplift,
            Commercial_Notes: commercialNotes,
            Shopped: shopped === "Yes" ? true : shopped === "No" ? false : shopped,
            WiFi: selectedWifi,
            catering_option: selectedCatering,
            Catering: currentOpsNotes ?? "",
            Flight_Operator_Test: [aircraftOp],
            Aircraft_Type_NEW: aircraftType,
            Aircraft_Category_NEW: aircraftCat,
            Tail_No: aircraftTail,
            Pilots: numPilots,
            Fuel_Stops: numFuelStops,
            Has_Flight_Attendant: hasFlightAttendant,
            FF_override: FFOverride,
        };

        pendingSettings.current = { Show_MOVIDA_in_Quote: selMovida, Preferred_Currency: selectedPrefCurrency };
        postData(formData, "/api/portal/update-deal", 0, "updateSalesDeal");

        //console.log(formData)
    };

    const onShowOptionsHandler = () => {
        setShowResultOptions(!showResultOptions);
    };

    const resetLegDataFromProps = () => {
        if (!legs?.length) return;

        legs.forEach((leg) => {
            legData.current[leg.id] = {
                id: leg.id,
                airport_From: leg.airport_From,
                airport_To: leg.airport_To,
                number_of_Bags: leg.number_of_Bags,
                number_of_Carry_On_Bags: leg.number_of_Carry_On_Bags,
                number_of_Passengers: leg.number_of_Passengers,
                Leg_Date_and_Time: leg.leg_Date_and_Time,
                daM_Ref: leg.daM_Ref,
            };
        });
        setParamLegs(legs);
    };

    const resetDealFieldsFromProps = () => {
        setSelectedKycHeat(deal.kyC_Heat);
        setSelectedPrefCurrency(savedSettings.Preferred_Currency);
        setSelMovida(savedSettings.Show_MOVIDA_in_Quote);
        setNumPets(deal.number_of_Pets ?? 0);
        setCurrentNotes(deal.notes_on_Flight);
        setContractName(deal.contract_Display_Name);
        setQuoteName(deal.quote_Display_Name);
        setUplift(deal.uplift ?? "");
        setCommercialNotes(deal.commercial_Notes ?? "");
        setShopped(toYesNoDisplay(deal.shopped) ?? "");
    };

    const handleCancelEdit = () => {
        resetLegDataFromProps();
        resetDealFieldsFromProps();
        setIsEditMode(false);
    };

    const handleSaveEdit = () => {
        updateDeal();
        if (isDealPreAvinode(deal)) {
            saveLegs();
        }
        setIsEditMode(false);
    };

    return (
        deal && (
            <>
                <div className="portal_sales_layout" key="sect-sales-deal">
                    <div className="deal_details_form">
                        <div>
                            <div className="formRow">
                                <label>Requested Date:</label>{" "}
                                {deal.date_Opened ? format(new Date(deal.date_Opened), "dd/MM/yy") : "-"}
                            </div>
                            <div className="formRow">
                                <label>Service:</label> {deal.service}
                            </div>
                            <div className="formRow">
                                <label>Trip Type:</label> {getTripTypeLabelFromLegCount(paramLegs.length) || "-"}
                            </div>
                            <div className="formRow">
                                <label>Preferred Currency:</label>{" "}
                                {isEditMode ? (
                                    <SelectInput
                                        value={(value) => setSelectedPrefCurrency(value)}
                                        options={currencyList}
                                        label=""
                                        defaultValue={selectedPrefCurrency}
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    (selectedPrefCurrency || deal.preferred_Currency || "-")?.toUpperCase()
                                )}
                            </div>
                            <div className="formRow">
                                <label>Uplift:</label>{" "}
                                {isEditMode ? (
                                    <TextInput
                                        defaultValue={deal.uplift ?? ""}
                                        label=""
                                        value={(fromValue) => setUplift(fromValue)}
                                    />
                                ) : (
                                    uplift || deal.uplift || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Notes:</label>{" "}
                                {isEditMode ? (
                                    <TextAreaInput
                                        maxLength={2000}
                                        showCharacterCount
                                        defaultValue={deal.notes_on_Flight}
                                        value={(fromValue) => setCurrentNotes(fromValue)}
                                        label=""
                                    />
                                ) : (
                                    currentNotes || deal.notes_on_Flight || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Commercial Notes:</label>{" "}
                                {isEditMode ? (
                                    <TextAreaInput
                                        maxLength={2000}
                                        showCharacterCount
                                        defaultValue={deal.commercial_Notes ?? ""}
                                        value={(fromValue) => setCommercialNotes(fromValue)}
                                        label=""
                                    />
                                ) : (
                                    commercialNotes || deal.commercial_Notes || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Shopped:</label>{" "}
                                {isEditMode ? (
                                    <SelectInput
                                        value={(value) => setShopped(value)}
                                        options={yesNoList}
                                        label=""
                                        defaultValue={toYesNoDisplay(deal.shopped) ?? ""}
                                        classNames={["select-input--full select-input--form"]}
                                    />
                                ) : (
                                    shopped || toYesNoDisplay(deal.shopped) || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Num Pets:</label>{" "}
                                {isEditMode ? (
                                    <fieldset className="fieldset fieldset--margin-bottom">
                                        <div className="text-input text-input--margin-bottom">
                                            <input
                                                type="text"
                                                autoComplete="on"
                                                className="text-input__input"
                                                defaultValue={deal.number_of_Pets ?? 0}
                                                onChange={(e) => setNumPets(parseInt(e.target.value))}
                                            />
                                            <label className="label label--minimised"></label>
                                        </div>
                                    </fieldset>
                                ) : (
                                    (numPets ?? deal.number_of_Pets ?? 0)
                                )}
                            </div>
                            <div className="formRow">
                                <label>Show Movida:</label>{" "}
                                <Switch
                                    controlled
                                    isOn={!!selMovida}
                                    disabled={settingsSaving || sending}
                                    classNames={["switch--gold", "switch--small"]}
                                    ariaLabel="Show MOVIDA in quote"
                                    value={(value) =>
                                        isEditMode
                                            ? setSelMovida(value)
                                            : saveQuoteSettings({ Show_MOVIDA_in_Quote: value })
                                    }
                                />
                                {savingSetting === "movida" && <PortalQuoteSettingStatus />}
                            </div>
                            <div className="formRow">
                                <label>Quote Name:</label>{" "}
                                {isEditMode ? (
                                    <TextInput
                                        defaultValue={quoteName}
                                        label=""
                                        value={(fromValue) => setQuoteName(fromValue)}
                                    />
                                ) : (
                                    quoteName || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>Contract Name:</label>{" "}
                                {isEditMode ? (
                                    <TextInput
                                        defaultValue={contractName}
                                        label=""
                                        value={(fromValue) => setContractName(fromValue)}
                                    />
                                ) : (
                                    contractName || "-"
                                )}
                            </div>
                            <div className="formRow">
                                <label>SHYF Number:</label> {deal.shyF_No}
                            </div>
                        </div>
                    </div>
                    <div className="deal_details_form">
                        <div
                            className={
                                isDealPreAvinode(deal)
                                    ? "portal-details__legs portal-details__legs-edit"
                                    : "portal-details__legs"
                            }
                        >
                            <PortalDealDetailsEditToolbar
                                isEditMode={isEditMode}
                                onEdit={() => {
                                    if (!settingsSaving) setIsEditMode(true);
                                }}
                                onSave={handleSaveEdit}
                                onCancel={handleCancelEdit}
                                sending={sending}
                            />
                            {paramLegs &&
                                paramLegs
                                    .sort((a, b) => {
                                        return a.leg_Number < b.leg_Number ? -1 : 1;
                                    })
                                    .map((leg, index) => {
                                        const isExpanded = expandedLegs[leg.id];
                                        const legDate = new Date(leg.leg_Date_and_Time);
                                        const arrivalDate =
                                            index === 0
                                                ? deal.leg_1_Date_of_Arrival
                                                    ? new Date(deal.leg_1_Date_of_Arrival)
                                                    : null
                                                : deal.leg_2_Date_of_Arrival
                                                  ? new Date(deal.leg_2_Date_of_Arrival)
                                                  : null;
                                        const arrivalTime =
                                            index === 0 ? deal.leg_1_Time_of_Arrival : deal.leg_2_Time_of_Arrival;
                                        const legEditable = isDealPreAvinode(deal) && isEditMode;

                                        return (
                                            <div key={leg.id} className="leg-section">
                                                <div
                                                    className="leg-header-clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-expanded={isExpanded}
                                                    onClick={() => toggleLeg(leg.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            toggleLeg(leg.id);
                                                        }
                                                    }}
                                                >
                                                    <strong>
                                                        Leg {index + 1} - {leg.airport_From} - {leg.airport_To}
                                                    </strong>
                                                    <img
                                                        src={chevronIcon}
                                                        className={
                                                            isExpanded ? "leg-header-clickable__chevron--open" : ""
                                                        }
                                                        alt=""
                                                    />
                                                </div>
                                                {isExpanded && (
                                                    <div
                                                        className={legEditable ? "leg-details leg-form" : "leg-details"}
                                                    >
                                                        {legEditable ? (
                                                            <>
                                                                <div className="formRow">
                                                                    <label>From:</label>
                                                                    <fieldset className="fieldset fieldset--margin-bottom">
                                                                        <div className="text-input text-input--margin-bottom">
                                                                            <input
                                                                                type="text"
                                                                                className="text-input__input"
                                                                                defaultValue={leg.airport_From}
                                                                                onChange={(e) =>
                                                                                    updateLegHandler(
                                                                                        leg,
                                                                                        "airport_From",
                                                                                        e
                                                                                    )
                                                                                }
                                                                            />
                                                                            <label className="label label--minimised"></label>
                                                                        </div>
                                                                    </fieldset>
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>To:</label>
                                                                    <fieldset className="fieldset fieldset--margin-bottom">
                                                                        <div className="text-input text-input--margin-bottom">
                                                                            <input
                                                                                type="text"
                                                                                className="text-input__input"
                                                                                defaultValue={leg.airport_To}
                                                                                onChange={(e) =>
                                                                                    updateLegHandler(
                                                                                        leg,
                                                                                        "airport_To",
                                                                                        e
                                                                                    )
                                                                                }
                                                                            />
                                                                            <label className="label label--minimised"></label>
                                                                        </div>
                                                                    </fieldset>
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Departure Date:</label>
                                                                    <fieldset className="fieldset fieldset--margin-bottom">
                                                                        <div className="text-input text-input--margin-bottom">
                                                                            <input
                                                                                type="date"
                                                                                className="text-input__input"
                                                                                defaultValue={format(
                                                                                    legDate,
                                                                                    "yyyy-MM-dd"
                                                                                )}
                                                                                onChange={(e) =>
                                                                                    updateLegDateHandler(leg, "date", e)
                                                                                }
                                                                            />
                                                                            <label className="label label--minimised"></label>
                                                                        </div>
                                                                    </fieldset>
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Departure Time:</label>
                                                                    <fieldset className="fieldset fieldset--margin-bottom">
                                                                        <div className="text-input text-input--margin-bottom">
                                                                            <input
                                                                                type="time"
                                                                                className="text-input__input"
                                                                                defaultValue={format(legDate, "HH:mm")}
                                                                                onChange={(e) =>
                                                                                    updateLegDateHandler(leg, "time", e)
                                                                                }
                                                                            />
                                                                            <label className="label label--minimised"></label>
                                                                        </div>
                                                                    </fieldset>
                                                                </div>
                                                                {arrivalDate && (
                                                                    <>
                                                                        <div className="formRow">
                                                                            <label>Arrival Date:</label>{" "}
                                                                            {format(arrivalDate, "dd/MM/yy")}
                                                                        </div>
                                                                        <div className="formRow">
                                                                            <label>Arrival Time:</label>{" "}
                                                                            {arrivalTime || "-"}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <div className="formRow">
                                                                    <label>Pax:</label>
                                                                    <fieldset className="fieldset fieldset--margin-bottom">
                                                                        <div className="text-input text-input--margin-bottom">
                                                                            <input
                                                                                type="text"
                                                                                autoComplete="on"
                                                                                className="text-input__input"
                                                                                defaultValue={leg.number_of_Passengers}
                                                                                onChange={(e) =>
                                                                                    updateLegHandler(
                                                                                        leg,
                                                                                        "number_of_Passengers",
                                                                                        e
                                                                                    )
                                                                                }
                                                                            />
                                                                            <label className="label label--minimised"></label>
                                                                        </div>
                                                                    </fieldset>
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Bags:</label>
                                                                    <fieldset className="fieldset fieldset--margin-bottom">
                                                                        <div className="text-input text-input--margin-bottom">
                                                                            <input
                                                                                type="text"
                                                                                autoComplete="on"
                                                                                className="text-input__input"
                                                                                defaultValue={leg.number_of_Bags}
                                                                                onChange={(e) =>
                                                                                    updateLegHandler(
                                                                                        leg,
                                                                                        "number_of_Bags",
                                                                                        e
                                                                                    )
                                                                                }
                                                                            />
                                                                            <label className="label label--minimised"></label>
                                                                        </div>
                                                                    </fieldset>
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Carry On:</label>
                                                                    <fieldset className="fieldset fieldset--margin-bottom">
                                                                        <div className="text-input text-input--margin-bottom">
                                                                            <input
                                                                                type="text"
                                                                                autoComplete="on"
                                                                                className="text-input__input"
                                                                                defaultValue={
                                                                                    leg.number_of_Carry_On_Bags
                                                                                }
                                                                                onChange={(e) =>
                                                                                    updateLegHandler(
                                                                                        leg,
                                                                                        "number_of_Carry_On_Bags",
                                                                                        e
                                                                                    )
                                                                                }
                                                                            />
                                                                            <label className="label label--minimised"></label>
                                                                        </div>
                                                                    </fieldset>
                                                                </div>
                                                                {paramLegs.length > 1 && onDeleteLeg && (
                                                                    <div className="formRow">
                                                                        <Button
                                                                            classNames={["button--tertiary"]}
                                                                            onClick={() => onDeleteLeg(leg)}
                                                                        >
                                                                            Delete leg
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="formRow">
                                                                    <label>From:</label> {leg.airport_From}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>To:</label> {leg.airport_To}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Departure Date:</label>{" "}
                                                                    {format(legDate, "dd/MM/yy")}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Departure Time:</label>{" "}
                                                                    {format(legDate, "HH:mm")}
                                                                </div>
                                                                {arrivalDate && (
                                                                    <>
                                                                        <div className="formRow">
                                                                            <label>Arrival Date:</label>{" "}
                                                                            {format(arrivalDate, "dd/MM/yy")}
                                                                        </div>
                                                                        <div className="formRow">
                                                                            <label>Arrival Time:</label>{" "}
                                                                            {arrivalTime || "-"}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <div className="formRow">
                                                                    <label>Pax:</label> {leg.number_of_Passengers}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Bags:</label> {leg.number_of_Bags}
                                                                </div>
                                                                <div className="formRow">
                                                                    <label>Carry On:</label>{" "}
                                                                    {leg.number_of_Carry_On_Bags}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                        </div>
                    </div>
                </div>

                {deal && showResultOptions && (
                    <>
                        <div className="portal__quote-options-close-wrapper">
                            <CloseCross colour={"white"} onClick={() => setShowResultOptions(false)} />
                        </div>
                        <PortalQuoteOptions
                            key={`${deal.id}-${savedSettings.Preferred_Currency}`}
                            deal={deal}
                            showMovida={savedSettings.Show_MOVIDA_in_Quote}
                            settingsBusy={settingsSaving || sending || isEditMode}
                            quoteSettings={({ disabled }) => (
                                <PortalQuoteSettings
                                    showMovida={savedSettings.Show_MOVIDA_in_Quote}
                                    currency={savedSettings.Preferred_Currency}
                                    savingSetting={savingSetting}
                                    disabled={disabled || settingsSaving || sending || isEditMode}
                                    onChange={saveQuoteSettings}
                                />
                            )}
                            onClose={() => setShowResultOptions(false)}
                            onDidReserveResult={() => setShowResultOptions(false)}
                            anchorOnLoad={openedFromBrokerLink}
                        />
                    </>
                )}

                {deal && firstLeg && (
                    <PortalSalesStageBasedCtas
                        deal={deal}
                        firstLeg={firstLeg}
                        onShowOptions={() => onShowOptionsHandler()}
                    />
                )}

                {settingsError && (
                    <p role="alert" className="quote__action-error">
                        {settingsError}
                    </p>
                )}
                {sending && <LoadingOverlay />}
            </>
        )
    );
};

export default PortalSalesDetails;
